import React, { useCallback, useMemo, useState } from 'react';
import { useTodoContext } from '../../contexts/TodoContext';
import { useToastContext } from '../../contexts/ToastContext';
import { TodoItem } from '../../types/todo';
import { TODAY } from '../../utils/constants';
import { getPriorityColor, getPriorityIcon } from '../../utils/priority';
import DailyReview from '../DailyReview';

interface TodayPanelProps {
  /** Collapsed state is owned by App so it survives reloads. */
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  /** Bring the corresponding node into view in the tree canvas. */
  onFocusNode?: (id: string) => void;
}

/**
 * TodayPanel - the day's committed task list, docked beside the tree.
 *
 * The tree is the map of everything; this panel is the short list of what the
 * day is actually for. Completed tasks stay visible until the next plan so the
 * day's progress is legible, and removing a row only takes the task off the
 * list — it never touches the tree.
 */
const TodayPanel: React.FC<TodayPanelProps> = ({ isCollapsed, onToggleCollapsed, onFocusNode }) => {
  const { tree, today, isDayPlanStale, hasPlannableWork, toggleDone, removeFromToday, reorderToday } = useTodoContext();
  const { showWarning } = useToastContext();

  const [showReview, setShowReview] = useState(false);
  const [isPromptDismissed, setIsPromptDismissed] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // The plan can outlive the tasks it points at only until the next prune, so
  // guard the render against ids that are momentarily missing.
  const entries = useMemo(
    () => today.items
      .map(id => ({ id, item: tree[id] }))
      .filter((entry): entry is { id: string; item: TodoItem } => Boolean(entry.item)),
    [today.items, tree]
  );

  const doneCount = entries.filter(entry => entry.item.done).length;
  const showPrompt = isDayPlanStale && hasPlannableWork && !isPromptDismissed && !showReview;

  const handleToggleDone = useCallback((id: string) => {
    toggleDone(id, incompleteChildren => {
      const names = incompleteChildren.map(child => child.item.name).join(', ');
      showWarning(`Cannot mark as complete. First complete these child tasks: ${names}`, 5000);
    });
  }, [toggleDone, showWarning]);

  const moveItem = useCallback((from: number, to: number) => {
    if (to < 0 || to >= today.items.length || from === to) return;
    const next = [...today.items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    reorderToday(next);
  }, [today.items, reorderToday]);

  const handleDrop = useCallback((targetIndex: number) => {
    if (draggedIndex === null) return;
    moveItem(draggedIndex, targetIndex);
    setDraggedIndex(null);
  }, [draggedIndex, moveItem]);

  const openReview = useCallback(() => {
    setIsPromptDismissed(true);
    setShowReview(true);
  }, []);

  return (
    <aside className={`today-panel ${isCollapsed ? 'collapsed' : ''}`} aria-label={TODAY.PANEL_TITLE}>
      <div className="today-panel-header">
        <button
          className="today-collapse-toggle"
          onClick={onToggleCollapsed}
          title={isCollapsed ? TODAY.EXPAND_TOOLTIP : TODAY.COLLAPSE_TOOLTIP}
          aria-label={isCollapsed ? TODAY.EXPAND_TOOLTIP : TODAY.COLLAPSE_TOOLTIP}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? '▶' : '▼'}
        </button>
        <h4 className="today-panel-title">{TODAY.PANEL_TITLE}</h4>
        {entries.length > 0 && (
          <span className="today-tally" data-testid="today-tally">
            {doneCount}/{entries.length} done
          </span>
        )}
        {!isCollapsed && entries.length > 0 && (
          <button className="today-plan-button" onClick={openReview}>
            {TODAY.REPLAN_BUTTON}
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>
          {showPrompt && (
            <div className="today-prompt" role="status">
              <span className="today-prompt-message">{TODAY.PROMPT_MESSAGE}</span>
              <div className="today-prompt-actions">
                <button className="today-plan-button" onClick={openReview}>
                  {TODAY.PLAN_BUTTON}
                </button>
                <button className="today-prompt-dismiss" onClick={() => setIsPromptDismissed(true)}>
                  {TODAY.PROMPT_DISMISS}
                </button>
              </div>
            </div>
          )}

          {entries.length === 0 ? (
            <div className="today-empty">
              <p className="today-empty-message">{TODAY.PANEL_EMPTY}</p>
              {/* The new-day prompt already offers this; don't stack two of them. */}
              {!showPrompt && (
                <button className="today-plan-button" onClick={openReview}>
                  {TODAY.PLAN_BUTTON}
                </button>
              )}
            </div>
          ) : (
            <ul className="today-list">
              {entries.map(({ id, item }, index) => (
                <li
                  key={id}
                  className={`today-row ${item.done ? 'is-done' : ''} ${draggedIndex === index ? 'is-dragging' : ''}`}
                  draggable
                  onDragStart={() => setDraggedIndex(index)}
                  onDragEnd={() => setDraggedIndex(null)}
                  onDragOver={event => event.preventDefault()}
                  onDrop={() => handleDrop(index)}
                >
                  <span className="today-drag-handle" aria-hidden="true">⋮⋮</span>

                  <input
                    type="checkbox"
                    className="today-row-checkbox"
                    checked={item.done}
                    onChange={() => handleToggleDone(id)}
                    aria-label={`Mark ${item.name} as ${item.done ? 'not done' : 'done'}`}
                  />

                  <span
                    className="today-row-priority"
                    style={{ color: getPriorityColor(item.priority) }}
                    title={`Priority: ${item.priority}`}
                  >
                    {getPriorityIcon(item.priority)}
                  </span>

                  <button
                    type="button"
                    className="today-row-name"
                    data-testid="today-row-name"
                    onClick={() => onFocusNode?.(id)}
                    title={TODAY.FOCUS_TOOLTIP}
                  >
                    {item.name}
                  </button>

                  <span className="today-row-actions">
                    <button
                      className="today-row-move"
                      onClick={() => moveItem(index, index - 1)}
                      disabled={index === 0}
                      title={TODAY.MOVE_UP_TOOLTIP}
                      aria-label={TODAY.MOVE_UP_TOOLTIP}
                    >
                      ↑
                    </button>
                    <button
                      className="today-row-move"
                      onClick={() => moveItem(index, index + 1)}
                      disabled={index === entries.length - 1}
                      title={TODAY.MOVE_DOWN_TOOLTIP}
                      aria-label={TODAY.MOVE_DOWN_TOOLTIP}
                    >
                      ↓
                    </button>
                    <button
                      className="today-row-remove"
                      onClick={() => removeFromToday(id)}
                      title={TODAY.REMOVE_TOOLTIP}
                      aria-label={TODAY.REMOVE_TOOLTIP}
                    >
                      ×
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {showReview && <DailyReview onClose={() => setShowReview(false)} />}
    </aside>
  );
};

export default TodayPanel;
